export const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
export const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "0%");
export const round1 = (v) => (typeof v === "number" ? Math.round(v * 10) / 10 : 0);

export const avg = (...values) => {
  const nums = values.filter((v) => typeof v === "number");
  if (!nums.length) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

export const formatBooks = (total) => {
  if (typeof total !== "number") return "—";
  if (total >= 1_000_000) return `${(total / 1_000_000).toFixed(1)}M`;
  if (total >= 1_000) return `${(total / 1_000).toFixed(1)}K`;
  return fmt(total);
};

export { formatGeneratedDate, buildScopeDataLabel, buildDataStatusChip } from "@/utils/dataFreshness";

export const getReadinessLabel = (score) => {
  if (score >= 85) return { label: "Strong Readiness", tone: "green" };
  if (score >= 70) return { label: "Moderate Readiness", tone: "amber" };
  return { label: "Low Readiness", tone: "amber" };
};

export const getSeverityFromPct = (value, { healthy = 85, watch = 70 } = {}) => {
  if (value >= healthy) return { severity: "Healthy", severityTone: "" };
  if (value >= watch) return { severity: "Watch", severityTone: "amber" };
  return { severity: "High priority", severityTone: "red" };
};

export const getEquitySeverity = (gapPct) => {
  if (gapPct <= 15) return { severity: "On track", severityTone: "green" };
  if (gapPct <= 35) return { severity: "Equity gap", severityTone: "red" };
  return { severity: "Critical gap", severityTone: "red" };
};

export const computeInclusionScore = (overview) => {
  const ramp = overview?.ramp_coverage ?? 0;
  const library = overview?.library_coverage ?? 0;
  const educatorCovered = 100 - (overview?.inclusion_gap_pct ?? 0);
  const dedicated = overview?.special_educator_dedicated_pct ?? 0;
  return round1(avg(ramp, library, educatorCovered, dedicated));
};

export const computeDataCoverage = (overview) => {
  const total = overview?.total_schools ?? 0;
  if (!total) return 0;
  const reported = (overview?.tap_water_yes ?? 0) + (overview?.tap_water_no ?? 0);
  return Math.min(100, round1((reported / total) * 100));
};

export const computeMetricConsistency = (overview) => {
  const pairs = [
    [overview?.tap_water_coverage ?? 0, overview?.purification_coverage ?? 0],
    [overview?.classroom_hygiene_coverage ?? 0, overview?.kitchen_hygiene_coverage ?? 0],
    [overview?.ramp_coverage ?? 0, overview?.library_coverage ?? 0],
  ];
  const gaps = pairs.map(([a, b]) => Math.abs(a - b));
  return round1(100 - avg(...gaps));
};

export const computeBlockCohesion = (blockData, field = "water_safety_index") => {
  const values = (blockData ?? []).map((b) => b[field] ?? 0).filter((v) => v > 0);
  if (values.length < 2) return 100;
  const spread = Math.max(...values) - Math.min(...values);
  return round1(Math.max(0, 100 - spread));
};

export const computeFieldValidationNeed = (overview) => {
  const total = overview?.total_schools ?? 0;
  if (!total) return 0;
  const needsValidation =
    (overview?.purification_non_func ?? 0) +
    (overview?.rwh_non_func ?? 0) +
    (overview?.special_educator_no ?? 0) +
    Math.max(0, total - (overview?.water_testing_yes ?? 0));
  return round1(Math.min(100, (needsValidation / total) * 100));
};

export const computeInsightConfidence = (overview) =>
  round1(avg(overview?.water_safety_index ?? 0, overview?.compliance_score ?? 0, computeDataCoverage(overview)));

export const getFocusBlockNames = (bottomBlocks, blockData, limit = 3, sortKey = "water_safety_index") => {
  const source = bottomBlocks?.length
    ? bottomBlocks
    : [...(blockData ?? [])].sort((a, b) => (a[sortKey] ?? 0) - (b[sortKey] ?? 0));
  return source
    .slice(0, limit)
    .map((b) => b.block_name)
    .filter(Boolean);
};

export const getLowBlocksByMetric = (blockData, field, limit = 3) =>
  [...(blockData ?? [])]
    .sort((a, b) => (a[field] ?? 0) - (b[field] ?? 0))
    .slice(0, limit)
    .map((b) => b.block_name)
    .filter(Boolean);

export const getFirstAidChipLabel = (firstAidPct) => {
  if (firstAidPct >= 90) return { label: `First Aid ${pct(firstAidPct)}`, tone: "green" };
  if (firstAidPct >= 75) return { label: `First Aid ${pct(firstAidPct)}`, tone: "amber" };
  return { label: `First Aid Gap ${pct(100 - firstAidPct, 1)}`, tone: "red" };
};

export const deriveDeadlineDays = (gapCount, totalSchools) => {
  if (!gapCount || !totalSchools) return 30;
  const ratio = gapCount / totalSchools;
  if (ratio > 0.35) return 7;
  if (ratio > 0.15) return 14;
  if (ratio > 0.05) return 21;
  return 30;
};

export const derivePriorityLabel = (gapCount, totalSchools) => {
  const ratio = gapCount / (totalSchools || 1);
  if (ratio > 0.25) return { label: "Critical", tone: "high" };
  if (ratio > 0.1) return { label: "High", tone: "medium" };
  if (ratio > 0.03) return { label: "Medium", tone: "low" };
  return { label: "Maintain", tone: "low" };
};

const coverageBelow = (value, threshold) =>
  typeof value === "number" && Number.isFinite(value) && value < threshold;

export const countPriorityGaps = (overview) => {
  if (!overview) return 0;
  return [
    coverageBelow(overview.purification_coverage, 75),
    coverageBelow(overview.rwh_coverage, 40),
    coverageBelow(overview.kitchen_hygiene_coverage, 80),
    (overview.inclusion_gap_pct ?? 0) > 30,
  ].filter(Boolean).length;
};

export const buildInsightsHeroSummary = (overview) => {
  const gaps = [];
  if (coverageBelow(overview?.purification_coverage, 75)) {
    gaps.push(`purification at ${pct(overview.purification_coverage)}`);
  }
  if (coverageBelow(overview?.rwh_coverage, 40)) {
    gaps.push(`RWH at ${pct(overview.rwh_coverage)} (${fmt(overview.rwh_non_func)} non-functional)`);
  }
  if (coverageBelow(overview?.kitchen_hygiene_coverage, 80)) {
    gaps.push(`kitchen hygiene at ${pct(overview.kitchen_hygiene_coverage)}`);
  }
  if ((overview?.inclusion_gap_pct ?? 0) > 30) {
    gaps.push(`inclusion gap at ${pct(overview.inclusion_gap_pct)}`);
  }
  if (!gaps.length) {
    return `District composite readiness is ${pct(overview?.compliance_score)} across ${fmt(overview?.total_schools)} schools with no major threshold breaches.`;
  }
  return `District status shows ${gaps.join(", ")}. WSI is ${round1(overview?.water_safety_index ?? 0).toFixed(1)} across ${fmt(overview?.total_schools)} schools.`;
};

export const buildExecutiveInsightCards = (overview) => {
  const educatorCoveredPct = round1(100 - (overview.inclusion_gap_pct ?? 0));
  const classroom = overview.classroom_hygiene_coverage ?? 0;
  const kitchen = overview.kitchen_hygiene_coverage ?? 0;
  const purif = overview.purification_coverage ?? 0;
  const inclusionGap = overview.inclusion_gap_pct ?? 0;

  return [
    {
      key: "classroom",
      severity: getSeverityFromPct(classroom, { healthy: 95, watch: 85 }),
      title: "Classroom Hygiene Is Strong",
      value: pct(classroom),
      description: `${fmt(overview.classroom_dustbin_all)} of ${fmt(overview.total_schools)} schools report classroom dustbin coverage at all classrooms (${pct(classroom)}).`,
      progress: classroom,
      progressTone: classroom >= 85 ? "green" : classroom >= 70 ? "orange" : "red",
    },
    {
      key: "kitchen",
      severity: getSeverityFromPct(kitchen, { healthy: 85, watch: 75 }),
      title: "Kitchen Hygiene Lags Classroom",
      value: pct(kitchen),
      description: `Kitchen dustbin coverage is ${pct(kitchen)} vs classroom ${pct(classroom)}, a ${pct(Math.abs(classroom - kitchen))} gap across ${fmt(overview.kitchen_dustbin_yes)} schools.`,
      progress: kitchen,
      progressTone: kitchen >= 75 ? "orange" : "red",
    },
    {
      key: "purification",
      severity: getSeverityFromPct(purif, { healthy: 80, watch: 70 }),
      title: "Water Purification Gap",
      value: pct(purif),
      description: `Tap water is ${pct(overview.tap_water_coverage)} but purification is ${pct(purif)}; ${fmt(overview.purification_no)} schools lack purification and ${fmt(overview.purification_non_func)} units are non-functional.`,
      progress: purif,
      progressTone: purif >= 70 ? "orange" : "red",
    },
    {
      key: "inclusion",
      severity: getEquitySeverity(inclusionGap),
      title: "Special Educator Gap Persists",
      value: pct(inclusionGap),
      description: `${fmt(overview.special_educator_no)} schools have no educator support; dedicated coverage is ${pct(overview.special_educator_dedicated_pct)} and cluster support is ${pct(overview.special_educator_cluster_pct)}.`,
      progress: educatorCoveredPct,
      progressTone: inclusionGap > 30 ? "red" : "orange",
    },
  ];
};

export const buildRootCauseSignals = (overview) => {
  const waterQualityGap = round1(Math.max(0, (overview.tap_water_coverage ?? 0) - (overview.purification_coverage ?? 0)));
  const testingGap = round1(Math.max(0, 100 - (overview.water_testing_coverage ?? 0)));
  const hygieneLift = round1(Math.max(0, (overview.classroom_hygiene_coverage ?? 0) - (overview.kitchen_hygiene_coverage ?? 0)));

  return [
    {
      key: "water-quality",
      title: "Water Quality Gap After Access",
      description: `Tap water is ${pct(overview.tap_water_coverage)} but purification is ${pct(overview.purification_coverage)}; ${fmt(overview.purification_no)} schools still need purification setup.`,
      score: pct(waterQualityGap),
      scoreLabel: "Gap",
    },
    {
      key: "rwh",
      title: "Low RWH Adoption and Uptime",
      description: `RWH coverage is ${pct(overview.rwh_coverage)} (${fmt(overview.rwh_yes)} schools); ${fmt(overview.rwh_non_func)} harvesting systems are non-functional.`,
      score: fmt(overview.rwh_non_func ?? 0),
      scoreLabel: "Non-functional",
    },
    {
      key: "inclusion",
      title: "Inclusion Staffing Shortfall",
      description: `Ramp coverage is ${pct(overview.ramp_coverage)} and library is ${pct(overview.library_coverage)}, but ${fmt(overview.special_educator_no)} schools report no special educator support.`,
      score: fmt(overview.special_educator_no),
      scoreLabel: "Schools",
    },
    {
      key: "hygiene",
      title: "Hygiene Discipline Gap (kitchen vs Classroom)",
      description: `Classroom hygiene is ${pct(overview.classroom_hygiene_coverage)} and toilet hygiene is ${pct(overview.toilet_hygiene_coverage)}, while kitchen hygiene trails by ${pct(hygieneLift)}.`,
      score: pct(overview.classroom_hygiene_coverage),
      scoreLabel: "Classroom",
    },
    {
      key: "testing",
      title: "Water Testing Follow-Up",
      description: `Testing coverage is ${pct(overview.water_testing_coverage)} with ${fmt(overview.water_testing_yes)} schools tested; ${pct(testingGap)} of schools still need validation.`,
      score: pct(testingGap),
      scoreLabel: "Untested share",
    },
  ];
};

const statusFromGapRatio = (ratio) => {
  if (ratio > 0.2) return { status: "Critical", statusTone: "red" };
  if (ratio > 0.08) return { status: "High", statusTone: "amber" };
  if (ratio > 0.02) return { status: "Monitor", statusTone: "green" };
  return { status: "Stable", statusTone: "green" };
};

export const buildPriorityActionQueue = (overview, blockData, bottomBlocks) => {
  const total = overview.total_schools || 1;
  const focusWater = getFocusBlockNames(bottomBlocks, blockData, 3, "water_safety_index").join(", ");
  const focusHygiene = getLowBlocksByMetric(blockData, "hygiene_score", 3).join(", ");
  const focusInclusion = getLowBlocksByMetric(blockData, "inclusion_gap_pct", 3).join(", ");
  const testingGap = Math.max(0, total - (overview.water_testing_yes ?? 0));
  const shedGap = Math.max(0, total - (overview.kitchen_shed_yes ?? 0));
  const gardenGap = Math.max(0, total - (overview.kitchen_garden_yes ?? 0));

  const items = [
    {
      issue: `Close purification gap (${fmt(overview.purification_no)} schools)`,
      blocks: focusWater || "—",
      target: `Raise to ${pct(Math.min(100, overview.purification_coverage + (overview.purification_no / total) * 100))}`,
      gap: overview.purification_no ?? 0,
      ...statusFromGapRatio((overview.purification_no ?? 0) / total),
    },
    {
      issue: `Repair non-functional RWH (${fmt(overview.rwh_non_func)} systems)`,
      blocks: `${fmt(overview.rwh_yes)} RWH schools`,
      target: `Restore ${fmt(overview.rwh_non_func)} units`,
      gap: overview.rwh_non_func ?? 0,
      ...statusFromGapRatio((overview.rwh_non_func ?? 0) / Math.max(overview.rwh_yes ?? 1, 1)),
    },
    {
      issue: `Improve kitchen hygiene (${fmt(total - (overview.kitchen_dustbin_yes ?? 0))} schools)`,
      blocks: focusHygiene || "—",
      target: `Reach ${pct(Math.min(100, overview.kitchen_hygiene_coverage + 10))}`,
      gap: total - (overview.kitchen_dustbin_yes ?? 0),
      ...statusFromGapRatio((total - (overview.kitchen_dustbin_yes ?? 0)) / total),
    },
    {
      issue: `Close inclusion gap (${fmt(overview.special_educator_no)} schools)`,
      blocks: focusInclusion || "—",
      target: `Reduce gap below ${pct(Math.max(0, overview.inclusion_gap_pct - 10))}`,
      gap: overview.special_educator_no ?? 0,
      ...statusFromGapRatio((overview.inclusion_gap_pct ?? 0) / 100),
    },
    {
      issue: `Validate water testing (${fmt(testingGap)} schools pending)`,
      blocks: focusWater || "—",
      target: `Lift testing to ${pct(Math.min(100, overview.water_testing_coverage + (testingGap / total) * 100))}`,
      gap: testingGap,
      ...statusFromGapRatio(testingGap / total),
    },
    {
      issue: `Kitchen shed provisioning (${fmt(shedGap)} schools)`,
      blocks: focusHygiene || "—",
      target: `Shed coverage ${pct(overview.kitchen_shed_coverage)} → ${pct(Math.min(100, overview.kitchen_shed_coverage + (shedGap / total) * 100))}`,
      gap: shedGap,
      ...statusFromGapRatio(shedGap / total),
    },
    {
      issue: `Kitchen garden scale-up (${fmt(gardenGap)} schools)`,
      blocks: focusHygiene || "—",
      target: `Garden coverage ${pct(overview.kitchen_garden_coverage)}`,
      gap: gardenGap,
      ...statusFromGapRatio(gardenGap / total),
    },
    {
      issue: `Sustain classroom hygiene (${fmt(overview.classroom_dustbin_all)} schools)`,
      blocks: "All blocks",
      target: `Maintain ${pct(overview.classroom_hygiene_coverage)}`,
      gap: overview.classroom_dustbin_no ?? 0,
      ...statusFromGapRatio((overview.classroom_dustbin_no ?? 0) / total),
    },
  ];

  return items
    .filter((item) => item.gap > 0 || item.issue.includes("Sustain"))
    .sort((a, b) => b.gap - a.gap)
    .slice(0, 6)
    .map((item, index) => ({ ...item, priority: index + 1 }));
};

export const buildInsightsClosurePlan = (overview, blockData, bottomBlocks) => {
  const focus = getFocusBlockNames(bottomBlocks, blockData, 3).join(", ");
  const validationCount =
    (overview.purification_non_func ?? 0) +
    (overview.rwh_non_func ?? 0) +
    (overview.special_educator_no ?? 0);

  return [
    {
      step: 1,
      title: "Days 1–7: Validate High-Risk Schools",
      description: `Confirm ${fmt(validationCount)} flagged schools across purification, RWH, kitchen hygiene and educator gaps${focus ? ` starting with ${focus}` : ""}.`,
    },
    {
      step: 2,
      title: "Days 8–15: Assign School-Wise Owners",
      description: `Map ${fmt(overview.purification_no)} purification gaps, ${fmt(overview.rwh_non_func)} RWH repairs and ${fmt(overview.special_educator_no)} inclusion cases to block owners.`,
    },
    {
      step: 3,
      title: "Days 16–25: Execute Repairs and Provisioning",
      description: `Target WSI lift from ${round1(overview.water_safety_index).toFixed(1)} by closing ${fmt(overview.purification_non_func)} purification and ${fmt(overview.rwh_non_func)} RWH exceptions.`,
    },
    {
      step: 4,
      title: "Days 26–30: Review and Certify Closure",
      description: `Review compliance at ${pct(overview.compliance_score)} and certify resolved items; escalate remaining ${fmt(overview.special_educator_no)} inclusion gaps.`,
    },
  ];
};

export const buildWaterActionQueue = (overview, blockData, bottomBlocks) => {
  const total = overview.total_schools || 1;
  const focus = getFocusBlockNames(bottomBlocks, blockData, 3).join(", ");
  const testingTarget = Math.min(100, overview.water_testing_coverage + Math.max(0, 95 - overview.water_testing_coverage) * 0.5);

  return [
    {
      title: "Close Purification Non-Availability",
      description: `Procurement plan for ${fmt(overview.purification_no)} schools without purification (${pct(overview.purification_coverage)} current coverage).`,
      priority: derivePriorityLabel(overview.purification_no, total),
      focus,
    },
    {
      title: "Repair Non-Functional Units",
      description: `Repair tickets for ${fmt(overview.purification_non_func)} non-functional units (${pct(overview.purification_non_func_pct)} of schools).`,
      priority: derivePriorityLabel(overview.purification_non_func, total),
      focus,
    },
    {
      title: `Raise Testing Coverage Toward ${pct(round1(testingTarget))}`,
      description: `${fmt(Math.max(0, total - (overview.water_testing_yes ?? 0)))} schools pending testing; current coverage ${pct(overview.water_testing_coverage)}.`,
      priority: derivePriorityLabel(Math.max(0, total - (overview.water_testing_yes ?? 0)), total),
      focus,
    },
    {
      title: "Rainwater Harvesting Adoption",
      description: `Expand from ${fmt(overview.rwh_yes)} schools (${pct(overview.rwh_coverage)}); repair ${fmt(overview.rwh_non_func)} non-functional systems.`,
      priority: derivePriorityLabel(overview.rwh_non_func, Math.max(overview.rwh_yes ?? 1, 1)),
      focus,
    },
  ];
};

export const buildHygieneActionQueue = (overview, blockData) => {
  const total = overview.total_schools || 1;
  const shedGap = Math.max(0, total - (overview.kitchen_shed_yes ?? 0));
  const kitchenGap = Math.max(0, total - (overview.kitchen_dustbin_yes ?? 0));
  const gardenGap = Math.max(0, total - (overview.kitchen_garden_yes ?? 0));
  const partialGap = (overview.classroom_dustbin_some ?? 0) + (overview.classroom_dustbin_no ?? 0);
  const focus = getLowBlocksByMetric(blockData, "kitchen_infra_score", 3).join(", ");

  return [
    {
      title: "Close Kitchen Shed Gaps",
      description: `${fmt(shedGap)} schools without kitchen shed (${pct(overview.kitchen_shed_coverage)} coverage). Focus: ${focus || "low MDM blocks"}.`,
      priority: derivePriorityLabel(shedGap, total),
    },
    {
      title: "Improve Kitchen Dustbin Coverage",
      description: `${fmt(kitchenGap)} schools below full kitchen dustbin readiness (current ${pct(overview.kitchen_hygiene_coverage)}).`,
      priority: derivePriorityLabel(kitchenGap, total),
    },
    {
      title: "Scale Kitchen Garden Adoption",
      description: `${fmt(gardenGap)} schools without kitchen garden (${pct(overview.kitchen_garden_coverage)} coverage).`,
      priority: derivePriorityLabel(gardenGap, total),
    },
    {
      title: "Verify Classroom and Toilet Bins",
      description: `${fmt(partialGap)} schools with partial or missing classroom bins; ${fmt(overview.toilet_dustbin_yes)} schools have toilet dustbins.`,
      priority: derivePriorityLabel(partialGap, total),
    },
  ];
};

export const buildHealthActionQueue = (overview, highRiskSchools) => {
  const total = overview.total_schools || 1;
  const criticalSchools = (highRiskSchools ?? []).filter((s) => (s.risk_count ?? 0) >= 5).length;
  const lifeSavingGap = Math.max(0, total - (overview.life_saving_yes ?? 0));
  const healthRecordGap = Math.max(0, total - (overview.health_record_yes ?? 0));
  const thermalGap = Math.max(0, total - (overview.thermal_yes ?? 0));

  return [
    {
      title: "Close Critical School Gaps First",
      description: `${fmt(criticalSchools)} schools at risk score 5+; ${fmt(highRiskSchools?.length ?? 0)} schools in high-risk watchlist.`,
      priority: derivePriorityLabel(criticalSchools, total),
      deadline: deriveDeadlineDays(criticalSchools, total),
    },
    {
      title: "Procure Life-Saving Equipment",
      description: `${fmt(lifeSavingGap)} schools missing life-saving equipment (${pct(overview.life_saving_coverage)} coverage).`,
      priority: derivePriorityLabel(lifeSavingGap, total),
      deadline: deriveDeadlineDays(lifeSavingGap, total),
    },
    {
      title: "Digitize Pending Health Records",
      description: `${fmt(healthRecordGap)} schools without digitized health records (${pct(overview.health_record_coverage)} coverage).`,
      priority: derivePriorityLabel(healthRecordGap, total),
      deadline: deriveDeadlineDays(healthRecordGap, total),
    },
    {
      title: "Expand Thermal Screening",
      description: `${fmt(thermalGap)} schools without thermal screening (${pct(overview.thermal_coverage)} coverage).`,
      priority: derivePriorityLabel(thermalGap, total),
      deadline: deriveDeadlineDays(thermalGap, total),
    },
  ];
};

export const buildInclusionActionQueue = (overview, blockData, rampNo, educatorNo) => {
  const total = overview.total_schools || 1;
  const focus = getLowBlocksByMetric(blockData, "inclusion_gap_pct", 5).join(", ");

  return [
    {
      title: "Assign Inclusion Support Route",
      description: `Map ${fmt(educatorNo)} schools without educator coverage (${pct(overview.inclusion_gap_pct)} gap).`,
      deadline: deriveDeadlineDays(educatorNo, total),
    },
    {
      title: `Close ${fmt(rampNo)} Ramp Exceptions`,
      description: `Validate ramp gaps against ${fmt(overview.ramp_yes)} schools already reporting ramps (${pct(overview.ramp_coverage)}).`,
      deadline: deriveDeadlineDays(rampNo, total),
    },
    {
      title: "Audit Library Resource Adequacy",
      description: `${fmt(overview.library_yes)} libraries with ${formatBooks(overview.total_books)} books (${fmt(Math.round(overview.avg_books_per_school ?? 0))} avg per school).`,
      deadline: deriveDeadlineDays(Math.max(0, total - (overview.library_yes ?? 0)), total),
    },
    {
      title: "Run Block Risk Review",
      description: focus ? `Prioritize ${focus} where inclusion gap and water risk are highest.` : "Review highest-risk blocks from block-wise data.",
      deadline: 30,
    },
  ];
};

export const buildInclusionClosurePlan = (overview, rampNo, educatorNo) => [
  {
    week: "Week 1",
    title: "Validate Gaps",
    description: `Freeze ${fmt(educatorNo)} educator gaps, ${fmt(rampNo)} ramp exceptions and ${fmt(Math.max(0, (overview.total_schools ?? 0) - (overview.library_yes ?? 0)))} schools without library coverage.`,
  },
  {
    week: "Week 2",
    title: "Assign Owners",
    description: `Allocate gaps across ${fmt(overview.total_schools)} schools: ${fmt(overview.special_educator_dedicated)} with dedicated educators, ${fmt(overview.special_educator_cluster)} cluster-supported.`,
  },
  {
    week: "Week 3",
    title: "Field Verification",
    description: `Verify ramp readiness (${pct(overview.ramp_coverage)}), library access (${pct(overview.library_coverage)}) and educator routes for ${fmt(educatorNo)} schools.`,
  },
  {
    week: "Week 4",
    title: "Review Closure",
    description: `Target inclusion score above ${pct(computeInclusionScore(overview) + 5)}; escalate unresolved ${fmt(educatorNo)} educator gaps.`,
  },
];

export const buildHealthClosurePlan = (overview, highRiskSchools) => {
  const critical = (highRiskSchools ?? []).filter((s) => (s.risk_count ?? 0) >= 5).length;
  const thermalGap = Math.max(0, (overview.total_schools ?? 0) - (overview.thermal_yes ?? 0));
  return [
    {
      step: "01",
      title: "Week 1: Validate Critical Schools",
      description: `Confirm ${fmt(critical)} risk-score-5 schools and ${fmt(highRiskSchools?.length ?? 0)} high-risk entries from live facility data.`,
    },
    {
      step: "02",
      title: "Week 2–3: Close Equipment Gaps",
      description: `Close thermal gap for ${fmt(thermalGap)} schools and life-saving gap for ${fmt(Math.max(0, (overview.total_schools ?? 0) - (overview.life_saving_yes ?? 0)))} schools.`,
    },
    {
      step: "03",
      title: "Week 4: Certify Readiness",
      description: `Certify schools above ${pct(avg(overview.medical_checkup_coverage, overview.first_aid_coverage, overview.health_record_coverage))} average health readiness.`,
    },
  ];
};

export const buildHygieneClosurePlan = (overview, blockData) => {
  const shedGap = Math.max(0, (overview.total_schools ?? 0) - (overview.kitchen_shed_yes ?? 0));
  const kitchenGap = Math.max(0, (overview.total_schools ?? 0) - (overview.kitchen_dustbin_yes ?? 0));
  const classGap = (overview.classroom_dustbin_some ?? 0) + (overview.classroom_dustbin_no ?? 0);
  const focus = getLowBlocksByMetric(blockData, "hygiene_score", 3).join(", ");
  return `Week 1: validate ${fmt(shedGap)} kitchen shed gaps${focus ? ` in ${focus}` : ""} • Week 2: assign owners for ${fmt(kitchenGap)} kitchen dustbin gaps • Week 3: close ${fmt(classGap)} classroom bin exceptions • Week 4: review MDM score at ${pct(avg(overview.classroom_hygiene_coverage, overview.toilet_hygiene_coverage, overview.kitchen_hygiene_coverage))}.`;
};

export const buildWaterClosurePlan = (overview, blockData, bottomBlocks) => {
  const focus = getFocusBlockNames(bottomBlocks, blockData, 3).join(", ");
  return `Week 1: validate ${fmt(overview.purification_no)} purification gaps${focus ? ` in ${focus}` : ""} • Week 2: repair ${fmt(overview.purification_non_func)} non-functional units • Week 3: testing sprint for ${fmt(Math.max(0, (overview.total_schools ?? 0) - (overview.water_testing_yes ?? 0)))} schools • Week 4: review WSI at ${round1(overview.water_safety_index).toFixed(1)}.`;
};

export const deriveOwnerFromIssue = (issue) => {
  const text = String(issue ?? "").toLowerCase();
  if (text.includes("purification") || text.includes("rwh") || text.includes("testing")) return "Water & Facilities Officer";
  if (text.includes("kitchen") || text.includes("shed") || text.includes("garden")) return "MDM Supervisor";
  if (text.includes("inclusion") || text.includes("educator")) return "Inclusive Education Officer";
  if (text.includes("classroom")) return "School Headmaster";
  if (text.includes("life-saving") || text.includes("thermal") || text.includes("health")) return "Health & Safety Officer";
  return "Block Education Officer";
};

export const deriveTestingTarget = (currentPct, totalSchools, testedYes) => {
  const gap = Math.max(0, (totalSchools ?? 0) - (testedYes ?? 0));
  if (!gap) return 100;
  return round1(Math.min(100, (currentPct ?? 0) + (gap / (totalSchools || 1)) * 100));
};

export const buildInclusionHeroSummary = (overview, inclusionScore) => {
  const readiness = getReadinessLabel(inclusionScore);
  return {
    readinessLabel: readiness.label,
    readinessTone: readiness.tone,
    summary: `Ramp coverage is ${pct(overview.ramp_coverage)} and library is ${pct(overview.library_coverage)}, but ${fmt(overview.special_educator_no)} schools (${pct(overview.inclusion_gap_pct)}) lack educator support. Inclusion readiness is ${pct(inclusionScore)}.`,
    action: `Close ${fmt(overview.special_educator_no)} educator gaps and validate ${fmt(Math.max(0, (overview.total_schools ?? 0) - (overview.ramp_yes ?? 0)))} ramp exceptions in highest-risk blocks first.`,
  };
};
